/**
 * 取素材目录给镜卡显示缩略图(批 B1,2026-08-05)。纯解析在 `referenceThumbs.ts`,这里只管取数。
 *
 * 三条取数腿,**都只在真有对应参考时才发请求**(纯图文片一条网络都不多打):
 * - 动作:`videoAssetService.listMotions()` 直接带 name/thumbUrl/url(24h 现签,无需再签);
 * - 人物:`characterService.get(characterId)` 的 materials 带 objectKey + thumbUrl;
 * - 产品:实体表里的 `product-images/…` key 走 `productService.presignImage` 现签。
 *
 * ★每个 key 只签一次(失败也不重试)—— 同 ProductImagePanel 踩过的那条:effect 依赖自己写的
 *   state 又去写它,不记「试过了」就会无限重签。
 */
import { useEffect, useRef, useState } from "react";

import { characterService } from "@/entities/character/api/characterService";
import { productService } from "@/entities/product/api/productService";

import { videoAssetService } from "../api/videoAssetService";
import type { RefCatalog } from "./referenceThumbs";
import type { FormState } from "./videoCreateTypes";

const PRODUCT_IMAGE_PREFIX = "product-images/";

/** 本单所有逐镜参考的 role 集合(决定要不要发那条腿的请求)。 */
function refRoles(form: FormState): Set<string> {
  const out = new Set<string>();
  for (const refs of form.references ?? []) {
    for (const r of refs ?? []) {
      if (r?.role) out.add(r.role);
    }
  }
  return out;
}

export function useReferenceThumbs(form: FormState): RefCatalog {
  const [motions, setMotions] = useState<RefCatalog["motions"]>({});
  const [materials, setMaterials] = useState<RefCatalog["characterMaterials"]>({});
  const [characterName, setCharacterName] = useState<string | null>(null);
  const [presigned, setPresigned] = useState<Record<string, string>>({});
  const triedKeys = useRef<Set<string>>(new Set());
  const motionsLoaded = useRef(false);

  const roles = refRoles(form);
  const needMotions = roles.has("motion");
  const characterId = form.characterId ?? null;

  // ① 动作库(一次就够;库是平台级的,不随单变)
  useEffect(() => {
    if (!needMotions || motionsLoaded.current) return;
    motionsLoaded.current = true;
    void videoAssetService
      .listMotions()
      .then((list) => {
        const map: NonNullable<RefCatalog["motions"]> = {};
        for (const m of list ?? []) {
          map[m.objectKey] = {
            name: m.name,
            category: m.category,
            thumbUrl: m.thumbUrl,
            videoUrl: m.url,
          };
        }
        setMotions(map);
      })
      .catch(() => {
        // 取不到就退化成「只有名字没有图」——不打断出片,也不弹错
      });
  }, [needMotions]);

  // ② 选中人物的物料(objectKey → 缩略图)
  useEffect(() => {
    if (!characterId) {
      setMaterials({});
      setCharacterName(null);
      return;
    }
    let cancelled = false;
    void characterService
      .get(characterId)
      .then((c) => {
        if (cancelled) return;
        const map: NonNullable<RefCatalog["characterMaterials"]> = {};
        for (const m of c.materials ?? []) {
          map[m.objectKey] = { label: m.label, thumbUrl: m.thumbUrl ?? m.url };
        }
        setMaterials(map);
        setCharacterName(c.name);
      })
      .catch(() => {
        if (!cancelled) setMaterials({});
      });
    return () => {
      cancelled = true;
    };
  }, [characterId]);

  // ③ 产品图现签(实体表里的 product-images/ key)
  useEffect(() => {
    const missing = (form.entities ?? [])
      .map((e) => e.key ?? "")
      .filter(
        (k) => k.startsWith(PRODUCT_IMAGE_PREFIX) && !presigned[k] && !triedKeys.current.has(k)
      );
    if (missing.length === 0) return;
    missing.forEach((k) => triedKeys.current.add(k));
    void Promise.all(
      missing.map((k) =>
        productService
          .presignImage(k)
          .then((r) => [k, r?.url] as const)
          .catch(() => null)
      )
    ).then((pairs) => {
      const add: Record<string, string> = {};
      for (const p of pairs) {
        if (p && typeof p[1] === "string") add[p[0]] = p[1];
      }
      if (Object.keys(add).length > 0) setPresigned((cur) => ({ ...cur, ...add }));
    });
  }, [form.entities, presigned]);

  const entities: NonNullable<RefCatalog["entities"]> = {};
  for (const e of form.entities ?? []) {
    entities[e.id] = { name: e.name, key: e.key, role: e.role };
  }

  return { motions, characterMaterials: materials, entities, presigned, characterName };
}
